import type { ApiConnectionPlatform } from '../../types/apiConnections';
import type { BadgeVariant } from '../../components/ui/badge';
import type { DriftKind } from '../../lib/adAccountDrift';
import type { ConnectionHealthRow, DriftRow, TokenStaleRow } from '../../types/connectionHealth';

// Pure view helpers for the Connection Health & Drift admin page. No I/O — the
// page renders their output. Unit-tested directly.

export const PLATFORM_LABEL: Record<ApiConnectionPlatform, string> = {
  metaads: 'Meta Ads',
  facebook: 'Facebook Page',
  instagram: 'Instagram',
  googlebusiness: 'Google Business',
  googleads: 'Google Ads',
  tiktok: 'TikTok',
};

// Only facebook/instagram/metaads/googlebusiness ever produce a failure (the
// profile refs we resolve), but the type is total for safety.
export const HEALTH_PLATFORM_OPTIONS: ApiConnectionPlatform[] = [
  'facebook',
  'instagram',
  'metaads',
  'googlebusiness',
];

// Token staleness reads the SocialAccount row directly, so unlike the failing-feed
// signal it covers googleads too — which is where the revoked-token problem
// actually surfaced.
export const TOKEN_PLATFORM_OPTIONS: ApiConnectionPlatform[] = [
  'googleads',
  'metaads',
  'facebook',
  'instagram',
  'googlebusiness',
];

export const DRIFT_KIND_LABEL: Record<DriftKind, string> = {
  'in-sync': 'In sync',
  unset: 'Ad account unset',
  drifted: 'Ad account drifted',
  'profile-disconnected': 'Profile disconnected',
  'no-profile': 'No profile',
};

export const DRIFT_KIND_VARIANT: Record<DriftKind, BadgeVariant> = {
  'in-sync': 'success',
  unset: 'warning',
  drifted: 'danger',
  'profile-disconnected': 'warning',
  'no-profile': 'neutral',
};

const META_FAMILY: ReadonlySet<ApiConnectionPlatform> = new Set([
  'facebook',
  'instagram',
  'metaads',
]);

// Where a failing connection's "Reconnect" button points. Meta-family token
// failures go to the Onboarding full re-auth (mode=page — the token is dead, a
// re-scope alone won't fix it). A GBP-only failure has no Meta reconnect, so it
// deep-links to the account detail (its API Connections + GBP connect live
// there). Mixed → treat as Meta (the dominant reconnect path).
export function healthReconnectHref(row: {
  accountId: string;
  failing: Array<{ platform: ApiConnectionPlatform }>;
}): string {
  const platforms = row.failing.map((f) => f.platform);
  const hasMeta = platforms.some((p) => META_FAMILY.has(p));
  if (hasMeta) return `/onboarding?reconnect=${encodeURIComponent(row.accountId)}&mode=page`;
  // googlebusiness-only (or unknown) → the account's connections section.
  return `/accounts/${encodeURIComponent(row.accountId)}`;
}

// Drift fix = re-scope the ad account to the profile's — the exact path the
// per-account ApiConnectionsSection drift chip already uses.
export function driftReconnectHref(row: Pick<DriftRow, 'accountId'>): string {
  return `/onboarding?reconnect=${encodeURIComponent(row.accountId)}&mode=ad-account`;
}

// Where a STALE-TOKEN row's "Reconnect" points. A dead grant needs a fresh OAuth,
// so the Meta family goes to the onboarding full re-auth (mode=page — a re-scope
// would not renew the token). The Google platforms have no fleet-level re-auth
// route: their connect buttons live on the account's Connections tab, so link
// there (that page's per-row chip then fires the connect in place).
export function tokenReconnectHref(row: Pick<TokenStaleRow, 'accountId' | 'platform'>): string {
  const id = encodeURIComponent(row.accountId);
  if (META_FAMILY.has(row.platform)) return `/onboarding?reconnect=${id}&mode=page`;
  return `/accounts/${id}?tab=connections`;
}

// ---- filtering ----

export type HealthFilter = { platform: ApiConnectionPlatform | 'all'; search: string };
export type DriftFilter = { kind: DriftKind | 'all'; search: string };
export type TokenFilter = { platform: ApiConnectionPlatform | 'all'; search: string };

export const EMPTY_HEALTH_FILTER: HealthFilter = { platform: 'all', search: '' };
export const EMPTY_DRIFT_FILTER: DriftFilter = { kind: 'all', search: '' };
export const EMPTY_TOKEN_FILTER: TokenFilter = { platform: 'all', search: '' };

function matchesText(haystacks: Array<string | null | undefined>, needle: string): boolean {
  const q = needle.trim().toLowerCase();
  if (q === '') return true;
  return haystacks.some((h) => (h ?? '').toLowerCase().includes(q));
}

export function filterHealthRows(
  rows: ConnectionHealthRow[],
  filter: HealthFilter,
): ConnectionHealthRow[] {
  return rows.filter((r) => {
    if (filter.platform !== 'all' && !r.failing.some((f) => f.platform === filter.platform)) {
      return false;
    }
    return matchesText([r.accountName, r.brandName, r.groupName, r.accountManagerName], filter.search);
  });
}

export function filterDriftRows(rows: DriftRow[], filter: DriftFilter): DriftRow[] {
  return rows.filter((r) => {
    if (filter.kind !== 'all' && r.kind !== filter.kind) return false;
    return matchesText(
      [
        r.accountName,
        r.brandName,
        r.groupName,
        r.accountManagerName,
        r.kyckAdAccountId,
        ...r.profileAdAccountIds,
      ],
      filter.search,
    );
  });
}

export function filterTokenStaleRows(rows: TokenStaleRow[], filter: TokenFilter): TokenStaleRow[] {
  return rows.filter((r) => {
    if (filter.platform !== 'all' && r.platform !== filter.platform) return false;
    return matchesText(
      [r.accountName, r.brandName, r.groupName, r.accountManagerName],
      filter.search,
    );
  });
}

// ---- formatting ----

// Relative "3h ago" / "2d ago" for a failure timestamp; absolute is the tooltip.
export function formatRelative(iso: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const diffMs = Math.max(0, nowMs - then);
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatAdAccountList(ids: string[]): string {
  return ids.length > 0 ? ids.join(', ') : '—';
}
